import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Plus, Download, X, Trash2 } from "lucide-react";

// Prefix a stored /api/files/... path with the backend origin so the
// receipt link opens in a new tab without the frontend routing catching it.
const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const DEFAULT_FORM = {
  donor_name: "",
  email: "",
  phone: "",
  pan: "",
  address: "",
  amount: "",
  campaign: "General",
  payment_mode: "CASH",
  reference_id: "",
  donation_date: new Date().toISOString().slice(0, 10),
  notes: "",
  send_email: true,
};

const CAMPAIGN_OPTIONS = ["General", "Education", "Healthcare", "Food Distribution", "Women Empowerment", "Emergency Relief", "Other"];
const PAYMENT_MODES = ["CASH", "UPI", "BANK", "CHEQUE", "OTHER"];

export default function AdminDonations() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/donations");
    setItems(r.data || []);
  };

  const total = items.filter((d) => d.status === "success").reduce((a, d) => a + (d.amount || 0), 0);

  const submit = async () => {
    if (!form.donor_name.trim() || !form.email.trim() || !form.amount) {
      toast.error("Donor name, email and amount are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/donations/manual", {
        ...form,
        amount: Number(form.amount),
      });
      toast.success(`Receipt ${data.receipt_no} generated`);
      const es = data.email_status || {};
      if (es.sent) toast.success("📧 Emailed to donor with receipt");
      else if (es.error) toast.error(`Email not sent: ${es.error}`, { duration: 6000 });
      setShowAdd(false);
      setForm(DEFAULT_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record donation");
    } finally { setSaving(false); }
  };

  const removeDonation = async (id) => {
    if (!window.confirm("Delete this donation record?")) return;
    try {
      await api.delete(`/admin/donations/${id}`);
      toast.success("Donation deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="heading text-2xl font-bold">Donations</h1>
          <p className="text-sm text-slate-500 mt-1">
            Total received: <b className="text-brand">₹{total.toLocaleString("en-IN")}</b>
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand text-white font-semibold text-sm hover:opacity-90"
          data-testid="add-manual-donation-btn"
        >
          <Plus size={16} /> Add Manual Donation
        </button>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Donor</th>
              <th className="p-3 font-semibold">Campaign</th>
              <th className="p-3 font-semibold">Amount</th>
              <th className="p-3 font-semibold">Mode</th>
              <th className="p-3 font-semibold">PAN</th>
              <th className="p-3 font-semibold">Receipt</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Date</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t border-slate-100" data-testid={`donation-${d.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{d.donor_name}</div>
                  <div className="text-xs text-slate-500">{d.email} · {d.phone}</div>
                </td>
                <td className="p-3">{d.campaign}</td>
                <td className="p-3 font-bold">₹{d.amount?.toLocaleString("en-IN")}</td>
                <td className="p-3 text-xs">
                  {d.source === "manual" ? (
                    <span className="text-indigo-700">{d.payment_mode || "MANUAL"}</span>
                  ) : (
                    <span className="text-slate-500">PayU</span>
                  )}
                </td>
                <td className="p-3 text-xs font-mono">{d.pan || "—"}</td>
                <td className="p-3">
                  {d.receipt_url ? (
                    <a href={ABS(d.receipt_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1 text-xs" data-testid={`receipt-${d.id}`}>
                      <Download size={13} /> {d.receipt_no || "PDF"}
                    </a>
                  ) : (<span className="text-xs text-slate-400">—</span>)}
                </td>
                <td className="p-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${
                    d.status === "success" ? "bg-emerald-100 text-emerald-700" :
                    d.status === "failed" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>{d.status}</span>
                </td>
                <td className="p-3 text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-3 text-right">
                  <button onClick={() => removeDonation(d.id)} className="p-2 text-red-500 hover:bg-red-50 rounded" title="Delete" data-testid={`del-donation-${d.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={9} className="p-14 text-center text-slate-500">
                <Heart size={36} className="mx-auto text-slate-300" />
                <div className="mt-3">No donations yet.</div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Manual donation modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="manual-donation-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mt-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="heading text-lg font-black">Add Manual Donation</h2>
                <p className="text-xs text-slate-500 mt-0.5">For offline donations (cash / UPI / bank transfer). Auto-generates 80G receipt.</p>
              </div>
              <button onClick={() => setShowAdd(false)} className="text-slate-500 hover:text-slate-900" data-testid="close-manual-donation-modal"><X size={20} /></button>
            </div>

            <div className="px-6 py-5 grid grid-cols-2 gap-4">
              <Field label="Donor Name *">
                <input value={form.donor_name}
                  onChange={(e) => setForm({ ...form, donor_name: e.target.value })}
                  placeholder="Rajesh Kumar"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="donor-name-input" />
              </Field>

              <Field label="Email *">
                <input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="donor@example.com"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="donor-email-input" />
              </Field>

              <Field label="Phone">
                <input value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 90000 00000"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="PAN Number" hint="Required for 80G if amount ≥ ₹10,000">
                <input value={form.pan}
                  onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                  placeholder="ABCDE1234F" maxLength={10}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono uppercase" />
              </Field>

              <Field label="Address" full>
                <textarea value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Donor's full address for 80G receipt"
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none" />
              </Field>

              <Field label="Amount (₹) *">
                <input type="number" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="5000" min="1"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                  data-testid="donation-amount-input" />
              </Field>

              <Field label="Campaign / Purpose">
                <select value={form.campaign}
                  onChange={(e) => setForm({ ...form, campaign: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                  {CAMPAIGN_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>

              <Field label="Payment Mode">
                <select value={form.payment_mode}
                  onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>

              <Field label="Reference / UTR / Cheque No.">
                <input value={form.reference_id}
                  onChange={(e) => setForm({ ...form, reference_id: e.target.value })}
                  placeholder="Optional"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Donation Date">
                <input type="date" value={form.donation_date}
                  onChange={(e) => setForm({ ...form, donation_date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              </Field>

              <Field label="Internal Notes" full>
                <textarea value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder="For admin's own reference (not on receipt)"
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm resize-none" />
              </Field>

              <label className="col-span-2 flex items-center gap-2 text-sm text-slate-700 mt-1">
                <input type="checkbox" checked={form.send_email}
                  onChange={(e) => setForm({ ...form, send_email: e.target.checked })}
                  className="rounded" />
                Email the 80G PDF receipt to donor immediately
              </label>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving}
                className="px-4 py-2 rounded-lg bg-brand text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60"
                data-testid="submit-manual-donation-btn">
                {saving ? "Saving…" : "Save & Generate Receipt"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Field({ label, hint, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
